import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useIsTextEnlarged } from "@/hooks/useIsTextEnlarged";
import { AccentIconTile } from "@/ui/molecules/AccentIconTile";
import { InlineHint } from "@/ui/molecules/InlineHint";
import { SheetStep } from "@/ui/Popups/SheetStep";
import { SheetStepButton } from "@/ui/Popups/SheetStepButton";
import { isShownAsShort } from "../gapsDayModel";
import { coverPromptFor, type GapsScopedShiftGroup } from "../gapsGroupSheets";
import { GAPS_DAY_LABEL_FORMAT } from "../gapsLabels";
import { GapsDateField } from "./GapsDateField";
import { GapsJobBadge } from "./GapsJobBadge";
import { GapsPostAddDirectlyRow } from "./GapsPostAddDirectlyRow";
import { GapsPostAutoApproveHint } from "./GapsPostAutoApproveHint";
import { GapsPostPrefillChips } from "./GapsPostPrefillChips";
import { GapsPostShiftFields } from "./GapsPostShiftFields";
import { useGapsPostShift } from "./useGapsPostShift";

type Props = {
	/** The group the post was started from, read live; `undefined` for a post started from nothing. */
	group: GapsScopedShiftGroup | undefined;
	/** The day's short groups, for a post started from nothing to prefill from. */
	shortGroups: readonly GapsScopedShiftGroup[];
	date: DateTime;
	/** The home's week shortfalls, for the Date row's picker. */
	homeWeekShortfalls: readonly number[] | undefined;
	/** The Date row's pick (the post's Date is always editable, APP-886). */
	onPickDate: (date: DateTime) => void;
	onPrefillFrom: (groupKey: string) => void;
	onClose: () => void;
};

const SHEET = "gaps-post-shift";

/**
 * "Post a shift" (P3-9): the classic post screen's fields in a sheet, prefilled from the group it was opened for —
 * its job or role groups, its window as the hours (`initialTimes`), its shortfall less what is already posted as the count — so the common case is
 * one tap on Post. Opened with no group (the bottom bar), it offers the day's short shifts to prefill from instead.
 *
 * "Fast grab" is not a per-post setting in this app: auto-approval is department configuration evaluated by the
 * backend, so the sheet only reports it, read-only, and never claims what THIS post will do (P3-9). There is no
 * send-to-agency here (it lives on the posted request) and no re-notify.
 */
export const GapsPostShiftStep: FC<Props> = ({ group, shortGroups, date, homeWeekShortfalls, onPickDate, onPrefillFrom, onClose }) => {
	const { t } = useTranslation();
	const isTextEnlarged = useIsTextEnlarged();
	const form = useGapsPostShift({ group, date, onPosted: onClose });

	const dayLabel = date.toFormat(GAPS_DAY_LABEL_FORMAT);
	// The group the sheet still describes — the one it opened for, while the job and hours are still that group's. Once
	// the user changes either, the heading and the hero stop comparing against it; the count stays as the user left it.
	const describedGroup = form.isOnPrefilledGroup ? group : undefined;
	// "Not missing par" on the printed value, as every gaps shortfall is compared (`isShownAsShort`).
	const isAtPar = describedGroup !== undefined && !isShownAsShort(coverPromptFor(describedGroup).short);

	return (
		<SheetStep
			stepKey={SHEET}
			dataTestId={SHEET}
			leading={
				describedGroup === undefined ? (
					<AccentIconTile size="sm" component="span">
						<AddCircleOutlineIcon />
					</AccentIconTile>
				) : (
					<GapsJobBadge group={describedGroup} dataTestId={`${SHEET}-job-badge`} />
				)
			}
			title={t("gaps.postSheet.title")}
			subtitle={
				group === undefined
					? t("gaps.postSheet.subtitleBlank", { day: dayLabel })
					: describedGroup === undefined
						? t("gaps.postSheet.startedFrom", { job: group.jobLabel, time: group.timeLabel, day: dayLabel })
						: t("gaps.postSheet.prefilledFrom", { job: group.jobLabel, time: group.timeLabel, day: dayLabel })
			}
			footer={
				<>
					<SheetStepButton tone="dismiss" onClick={onClose} disabled={form.isPosting} dataTestId={`${SHEET}-cancel`}>
						{t("cancel")}
					</SheetStepButton>
					<SheetStepButton tone="confirm" onClick={() => void form.post()} disabled={!form.canPost} loading={form.isPosting} dataTestId={`${SHEET}-confirm`}>
						{/* At Android's larger font sizes "Post 2 shifts" crowds Cancel off its half of the footer; the page is
						    already titled Post a shift, so the count alone says enough. */}
						{t(isTextEnlarged ? "gaps.postSheet.confirmShort" : "gaps.postSheet.confirm", { count: form.neededNurses })}
					</SheetStepButton>
				</>
			}
		>
			{/* The day leads the page, as the design draws it (APP-886): the shifts offered below are that day's. */}
			<Box sx={{ px: 2, pt: 2.25 }}>
				<GapsDateField date={date} homeWeekShortfalls={homeWeekShortfalls} onPick={onPickDate} disabled={form.isPosting} testIdPrefix={SHEET} />
			</Box>
			{group === undefined && <GapsPostPrefillChips shortGroups={shortGroups} dayLabel={dayLabel} onPrefillFrom={onPrefillFrom} testIdPrefix={SHEET} />}
			{/* Why Post stays off for a section that cannot say which job it is — the home's group says the same. */}
			{group !== undefined && form.isWriteBlocked && (
				<Box sx={{ px: 2, pt: 2 }}>
					<InlineHint tone="warning" dataTestId={`${SHEET}-ambiguous-job`}>
						{t("gaps.group.ambiguousJob", { job: group.jobLabel })}
					</InlineHint>
				</Box>
			)}

			{/* A shift that is not short needs a person, not a post: adding someone directly leads (APP-886). */}
			{isAtPar && describedGroup !== undefined && (
				<GapsPostAddDirectlyRow group={describedGroup} date={date} isLeading disabled={form.isPosting} testIdPrefix={SHEET} />
			)}

			<GapsPostShiftFields form={form} describedGroup={describedGroup} date={date} testIdPrefix={SHEET} />

			<GapsPostAutoApproveHint onClose={onClose} testIdPrefix={SHEET} />

			{!isAtPar && <GapsPostAddDirectlyRow group={describedGroup} date={date} isLeading={false} disabled={form.isPosting} testIdPrefix={SHEET} />}
		</SheetStep>
	);
};
